import React, { createContext, useContext, useState, useCallback } from "react";
import { T } from "../../config/theme";
import { Icon, Icons } from "./Icons";

/* ─── Context ────────────────────────────────────────────────────────────────── */
const AlertContext = createContext(null);

export function useAlert() {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error("useAlert must be used inside AlertProvider");
  return ctx;
}

/* ─── Config per type ────────────────────────────────────────────────────────── */
const TYPE_CFG = {
  error:   { color: T.red,    bg: `${T.red}12`,    border: `${T.red}30`,    icon: Icons.x,       label: "Error"   },
  warning: { color: T.yellow, bg: `${T.yellow}12`, border: `${T.yellow}30`, icon: Icons.info,    label: "Warning" },
  success: { color: T.green,  bg: `${T.green}12`,  border: `${T.green}30`,  icon: Icons.check,   label: "Success" },
  info:    { color: T.blue,   bg: `${T.blue}12`,   border: `${T.blue}30`,   icon: Icons.info,    label: "Info"    },
};

/* ─── Modal renderer ─────────────────────────────────────────────────────────── */
function AlertModal({ alert, onClose }) {
  if (!alert) return null;
  const { type = "error", title, message, action } = alert;
  const cfg = TYPE_CFG[type] || TYPE_CFG.error;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,.78)", backdropFilter: "blur(16px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#111115", border: `1px solid rgba(255,255,255,.08)`,
          borderRadius: 22, maxWidth: 400, width: "100%", padding: "32px 28px",
          position: "relative", boxShadow: "0 40px 100px rgba(0,0,0,.7)",
          animation: "sl-scaleIn .28s cubic-bezier(.22,1,.36,1)",
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 14, right: 14, width: 30, height: 30,
            borderRadius: 8, border: `1px solid rgba(255,255,255,.07)`,
            background: "transparent", color: T.text3, display: "flex",
            alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14,
          }}
        >✕</button>

        {/* Icon circle */}
        <div style={{
          width: 56, height: 56, borderRadius: 16, marginBottom: 18,
          background: cfg.bg, border: `1px solid ${cfg.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon d={cfg.icon} size={24} color={cfg.color} />
        </div>

        {/* Title */}
        <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-.4px", marginBottom: 8 }}>
          {title || cfg.label}
        </div>

        {/* Message */}
        <div style={{ fontSize: 13, color: T.text2, lineHeight: 1.65, marginBottom: 22 }}>
          {message || "An unexpected error occurred. Please try again."}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
          {action && (
            <button
              onClick={() => { action.onClick?.(); onClose(); }}
              style={{
                padding: "12px 20px", borderRadius: 11, border: "none",
                background: cfg.color, color: "#fff", fontSize: 13, fontWeight: 700,
                cursor: "pointer", fontFamily: T.font, width: "100%",
              }}
            >
              {action.label}
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              padding: "11px 20px", borderRadius: 11, border: `1px solid rgba(255,255,255,.08)`,
              background: "transparent", color: T.text2, fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: T.font, width: "100%",
            }}
          >
            {action ? "Dismiss" : "OK"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Provider ───────────────────────────────────────────────────────────────── */
export function AlertProvider({ children }) {
  const [alert, setAlert] = useState(null);

  const showAlert = useCallback(({ type = "error", title, message, action } = {}) => {
    setAlert({ type, title, message, action });
  }, []);

  const dismiss = useCallback(() => setAlert(null), []);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <AlertModal alert={alert} onClose={dismiss} />
    </AlertContext.Provider>
  );
}
